/**
 * TrialFeedbackDialog Component
 * 
 * Collects feedback from users who don't upgrade after their trial expires.
 * This helps understand why users choose not to continue with a paid subscription.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

interface TrialFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userEmail?: string;
}

const TRIAL_FEEDBACK_REASONS = [
  { value: 'price', label: 'Too expensive' },
  { value: 'features', label: 'Missing features I need' },
  { value: 'not_ready', label: 'Not ready to commit yet' },
  { value: 'found_alternative', label: 'Found a better alternative' },
  { value: 'trial_too_short', label: 'Trial period too short' },
  { value: 'technical_issues', label: 'Experienced technical issues' },
  { value: 'other', label: 'Other reason' }
];

const TrialFeedbackDialog: React.FC<TrialFeedbackDialogProps> = ({
  open,
  onClose,
  userId,
  userEmail
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [additionalFeedback, setAdditionalFeedback] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) return;

    setSubmitting(true);
    try {
      // Store feedback in Firebase
      await addDoc(collection(db, 'trial_feedback'), {
        userId,
        userEmail: userEmail || null,
        reason: selectedReason,
        additionalFeedback: additionalFeedback.trim() || null,
        submittedAt: serverTimestamp(),
        trialEndedAt: new Date().toISOString()
      });

      setSubmitted(true);
      
      // Close dialog after 2 seconds
      setTimeout(() => {
        onClose();
        // Reset form for next time
        setSelectedReason('');
        setAdditionalFeedback('');
        setSubmitted(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting trial feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitted) {
      // Allow closing without submitting (optional - you might want to require feedback)
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!submitted}
    >
      <DialogTitle>
        {submitted ? 'Thank You!' : 'We\'d Love Your Feedback'}
      </DialogTitle>
      <DialogContent>
        {submitted ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Your feedback has been submitted. Thank you for helping us improve!
          </Alert>
        ) : (
          <>
            <Typography variant="body1" paragraph>
              We noticed your free trial has ended. Your feedback would help us improve FormCraft.
            </Typography>
            
            <FormControl component="fieldset" sx={{ mt: 2, mb: 3, width: '100%' }}>
              <FormLabel component="legend">
                Why didn't you upgrade? <Typography component="span" color="error">*</Typography>
              </FormLabel>
              <RadioGroup
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
              >
                {TRIAL_FEEDBACK_REASONS.map((reason) => (
                  <FormControlLabel
                    key={reason.value}
                    value={reason.value}
                    control={<Radio />}
                    label={reason.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional comments (optional)"
              value={additionalFeedback}
              onChange={(e) => setAdditionalFeedback(e.target.value)}
              placeholder="Tell us more about your decision..."
              sx={{ mt: 2 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!submitted && (
          <>
            <Button onClick={handleClose} disabled={submitting}>
              Skip
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={!selectedReason || submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TrialFeedbackDialog;
